import {
  Page,
  View,
  Text,
  Document,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import Html from 'react-pdf-html';

import {
  styles,
  Header,
  Footer,
  GrayLine,
  BrokenGrayLine,
  StandardPDFColors,
} from './common/CommonPDFComponents';

import { WebSocketMessage } from '../../../components/viewReport/messages';

import { sanitizeHTMLStringForPDFRender } from '~/utils/pdfs';

interface FormattedMessage {
  sender: string;
  date: string;
  text: string;
}
const documentStyles = StyleSheet.create({
  messages: {
    ...styles.sizeBody,
    color: StandardPDFColors.descriptionTextGray,
    marginTop: 12,
    marginBottom: 20,
    lineHeight: 1.7,
  },
});

export const formattedTextStyles = {
  h1: {
    fontSize: '28px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h2: {
    fontSize: '21px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h3: {
    fontSize: '16px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h4: {
    fontSize: '14px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: '12px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  h6: {
    fontSize: '9px',
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  p: {
    margin: 0,
    lineHeight: 1.5,
    color: StandardPDFColors.gray,
  },
  ul: {
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
  ol: {
    color: StandardPDFColors.gray,
    margin: 0,
    lineHeight: 1.5,
  },
};

const processMessages = (
  messages: WebSocketMessage[],
  accessToken: string | null,
) => {
  const tokens: string[] = accessToken ? accessToken.split('.') : ['', ''];

  const { user_id: userId } = JSON.parse(atob(tokens[1]));

  const getMessageSender = (id: number) => (id === userId ? 'You' : 'Company');

  const processedMessages: FormattedMessage[] = messages.map((messageInfo) => {
    const formattedDate = new Date(messageInfo.createdAt).toLocaleString(
      import.meta.env.VITE_LOCALE,
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      },
    );

    return {
      sender: getMessageSender(messageInfo.fromUser),
      date: formattedDate,
      text: messageInfo.message,
    };
  });

  return processedMessages;
};

const MessagesHeader = () => (
  <>
    <View>
      <Text style={{ ...styles.sizeHeading, ...styles.bold }}>Messages</Text>
    </View>
    <View style={{ marginTop: 6, marginBottom: 1 }}>
      <GrayLine />
    </View>
  </>
);

const MessageView = ({ sender, date, text }: FormattedMessage) => (
  <View style={{ marginTop: 12 }} wrap={false}>
    <View>
      <Text style={{ ...styles.sizeSubHeading, ...styles.medium }}>
        {sender}
      </Text>
      <Text
        style={{
          ...styles.sizeBody,
          color: StandardPDFColors.descriptionTextGray,
          marginTop: 2,
        }}
      >
        {date}
      </Text>
    </View>
    <View style={documentStyles.messages}>
      <Html
        stylesheet={formattedTextStyles}
        style={{
          fontSize: 12,
          color: StandardPDFColors.descriptionTextGray,
        }}
      >
        {sanitizeHTMLStringForPDFRender(text)}
      </Html>
    </View>
  </View>
);

const getMessagesDocument = (messageList: FormattedMessage[]) => (
  <Document>
    <Page size='A4' style={styles.page} wrap>
      <Header />
      <View style={styles.main}>
        <MessagesHeader />
        {messageList.map((message, index) => (
          <View key={index}>
            <MessageView
              sender={message.sender}
              text={message.text}
              date={message.date}
            />
            {index + 1 < messageList.length && <BrokenGrayLine />}
          </View>
        ))}
      </View>
      <Footer />
    </Page>
  </Document>
);

const generateMessageReportPDF = async (
  messages: WebSocketMessage[],
  accessToken: string | null,
) => {
  const processedMessages = processMessages(messages, accessToken);
  const generatedPDF = getMessagesDocument(processedMessages);
  const pdfBlob = await pdf(generatedPDF).toBlob();
  return pdfBlob;
};

export { generateMessageReportPDF };
